import { useQueryParams } from '@hooks/queryParams';
import { formatDate } from '@utils/dateHelpers';
import { camelToSnakeCase, snakeToCamelCase } from '@utils/textHelpers';
import {
  WorkloadExtendedSpeciality,
  WorkloadMonthlyCalendarItems,
  WorkloadMonthlyCalendarRespSuccess,
} from './types';
import { ApiError } from '@root/globalTypes';
import { queryKeys } from '@utils/consts';
import { useQuery } from '@tanstack/react-query';
import { queryFetch } from '@services/api';

export const useGetWorkloadMonthlyCalendar = (date: Date) => {
  const { params, parseParams } = useQueryParams();

  const { type, team, search, status, constructionTypes, unassigned } = params;
  const noOfWeeks = type === 'two-week' ? 2 : undefined;

  const queryParams = parseParams({
    date: formatDate(date),
    noOfWeeks,
    search,
    status,
    constructionTypes,
    team: team?.map((el) => camelToSnakeCase(el)),
    unassigned,
  });

  const { data, error, isFetching } = useQuery<WorkloadMonthlyCalendarRespSuccess, ApiError>({
    queryKey: noOfWeeks
      ? [...queryKeys.workloadTwoWeeklyCalendar, queryParams]
      : [...queryKeys.workloadMonthlyCalendar, queryParams],
    queryFn: () => queryFetch({ url: `workloads/monthly${queryParams}` }),
    placeholderData: (previousData) => previousData as WorkloadMonthlyCalendarRespSuccess,
    enabled: ['two-week', 'month'].includes(type || ''),
    select: (d) => {
      const parsedData: WorkloadMonthlyCalendarItems = {};

      Object.entries(d.records).forEach(([key, records]) => {
        // eslint-disable-next-line immutable/no-mutation
        parsedData[key] = records.map((el) => ({
          ...el,
          speciality: snakeToCamelCase(el.speciality) as WorkloadExtendedSpeciality,
        }));
      });

      return { ...d, data: parsedData };
    },
  });

  return {
    data,
    error,
    isLoading: isFetching,
  };
};
